import { ApiError } from 'src/utils/error';
import { getRewriteApplicantPortalUrl } from 'src/utils/rewrite-portal';

import { getBaseApiUrl, getBaseUrl } from '../utils/axios';
import { RequestObjectI } from './request-objects';

export interface RequestDataI {
  body?: object;
  query?: object;
  params?: object;
}

interface BaseRequestI {
  url: string;
  args: RequestInit;
}

const buildQuery = (query: object): string =>
  Object.keys(query)
    // @ts-ignore
    .map((key) => key + '=' + query[key])
    .join('&');

const replaceWildCards = (path: string, params: object) => {
  const wildCardKeys = Object.keys(params);
  for (const wKey of wildCardKeys) {
    const regex = new RegExp(':' + wKey);
    // @ts-ignore
    path = path.replace(regex, params[wKey]);
  }
  return path;
};

const buildPath = (req: RequestObjectI, data?: RequestDataI): string => {
  let path = req.url;
  if (data) {
    const { params, query } = data;
    if (params) {
      if (path.includes(':')) {
        path = replaceWildCards(path, params);
      }
    }
    if (query) {
      path += `?${buildQuery(query)}`;
    }
  }
  return path;
};

/**
 * L2 backstop: the store this applicant is hitting has been flipped to the
 * rewrite (`stores.on_rewrite = true`). The legacy API returns 403 with
 * `error: 'STORE_MIGRATED_TO_REWRITE'` — send the applicant to the rewrite
 * portal. The redirect is async, so callers still throw/return afterward;
 * nothing should proceed on this body.
 */
const redirectIfStoreMigrated = (body: any): void => {
  if (body && typeof body === 'object' && body.error === 'STORE_MIGRATED_TO_REWRITE') {
    window.location.assign(body.redirect_url ?? getRewriteApplicantPortalUrl());
  }
};

const baseRequest = (requestObject: RequestObjectI, data?: RequestDataI): BaseRequestI => {
  const url = buildPath(requestObject, data);
  const baseApiUrl = getBaseApiUrl();
  
  // Handle relative URLs (for iframe/proxy scenarios)
  // For aggregator routes in iframe, we need /api prefix for proxy to work
  // For absolute URLs, check if baseApiUrl already includes /api or if we need to add it
  let fullUrl: string;
  if (baseApiUrl) {
    // Absolute URL - check if baseApiUrl ends with /api or if we need to add it
    const baseEndsWithApi = baseApiUrl.endsWith('/api');
    const needsApiPrefix = url.startsWith('aggregator/') && !baseEndsWithApi;
    fullUrl = needsApiPrefix 
      ? `${baseApiUrl}/api/${url}`
      : `${baseApiUrl}/${url}`;
  } else {
    // Relative URL - always add /api prefix for aggregator endpoints (for proxy)
    const needsApiPrefix = url.startsWith('aggregator/');
    fullUrl = needsApiPrefix 
      ? `/api/${url}`
      : `/${url}`;
  }
  
  return {
    url: fullUrl,
    args: {
      method: requestObject.method,
      body: data?.body ? JSON.stringify(data.body) : undefined,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'Access-Control-Allow-Origin': getBaseUrl(),
      },
    },
  };
};

export const apiRequest = async <ResponseBody = any>(
  requestObject: RequestObjectI,
  data?: RequestDataI,
): Promise<ResponseBody> => {
  try {
    const { url, args } = baseRequest(requestObject, data);
    const response = await fetch(url, args);
    if (response) {
      const body = await parseJson(response);
      if (response.ok) {
        return body as ResponseBody;
      } else {
        redirectIfStoreMigrated(body);
        const error: ApiError = new Error('API_ERROR') as ApiError;
        error.statusCode = body.statusCode;
        if (!url.includes('/auth/validate') && !url.includes('/auth/login')) {
          // they session has expired
          if (body.statusCode === 401) {
            window.location.reload();
          }
        }

        error.statusMessage = body.message;
        error.body = body;
        throw error;
      }
    }
  } catch (e) {
    throw e;
  }

  return void 0 as ResponseBody;
};

export const otpRequest = async (requestObject: RequestObjectI, data?: RequestDataI) => {
  try {
    const { url, args } = baseRequest(requestObject, data);
    const response = await fetch(url, args);
    if (response) {
      const body = await parseJson(response);
      if (response.ok) {
        return body;
      } else {
        redirectIfStoreMigrated(body);
        const error: ApiError = new Error(body.message) as ApiError;
        error.statusCode = body.statusCode;
        error.statusMessage = body.message;
        error.body = body;
        throw error;
      }
    }
  } catch (e) {
    throw e;
  }
};

export const apiRequestNoRedirect = async (requestObject: RequestObjectI, data?: RequestDataI) => {
  try {
    const { url, args } = baseRequest(requestObject, data);
    const response = await fetch(url, args);
    if (response) {
      const body = await parseJson(response);
      if (response.ok) {
        return body;
      } else {
        redirectIfStoreMigrated(body);
        const error: ApiError = new Error('API_ERROR') as ApiError;
        error.statusCode = body.statusCode;

        error.statusMessage = body.message;
        error.body = body;
        throw error;
      }
    }
  } catch (e) {
    throw e;
  }
};

const parseJson = async (response: any) => {
  const text = await response.text();
  try {
    const json = JSON.parse(text);
    return json;
  } catch (err) {
    // throw new Error("Did not receive JSON, instead received: " + text);
    return text;
  }
};

export const apiRequestSync = (requestObject: RequestObjectI, data?: RequestDataI) => {
  try {
    const { url, args } = baseRequest(requestObject, data);
    return fetch(url, args)
      .then((res) => res.json())
      .then((body) => {
        redirectIfStoreMigrated(body);
        return body;
      });
  } catch (e) {
    console.log('Failed Request', e);
    return e.message;
  }
};
